import { StarRatingItem } from '@repo/ui-components/tailwind/Search/Refinements/RatingStars';
import { isSportdog, isPetsafe } from '@repo/utils/isSite';
export const LAST_ALGOLIA_QUERY_ID_KEY = 'lastAlgoliaQueryId';
export const PLP_ALGOLIA_QUERY_ID_KEY = 'plpAlgoliaQueryId';
export const PLP_ALGOLIA_QUERY_TIMESTAMP_KEY = 'plpAlgoliaQueryTimestamp';
export const RECOMMEND_METADATA_KEY = 'algoliaRecommendMetadata';
export const USER_TOKEN_KEY = 'uuid';
export const ALGOLIA_BOOLEAN_FACET_ATTRIBUTES = [
  'freeShippingEligible',
  'onSale',
];
export const COLLECTION_HIERARCHY_ATTRIBUTE = 'hierarchicalCategories.lvl0';

/**
 * Maximum hierarchical category level (0-indexed).
 * SportDog uses 4 levels (lvl0–lvl3); all other brands use 3 levels (lvl0–lvl2).
 */
export const MAX_HIERARCHY_LEVEL =
  process.env.GATSBY_SHOPIFY_SHOP_NAME?.includes('sportdog') ? 3 : 2;

/**
 * Builds the hierarchical attributes array for a given base attribute name,
 * spanning from lvl0 up to MAX_HIERARCHY_LEVEL.
 *
 * @example
 * // SportDog: ['hierarchicalCategories.lvl0', ..., 'hierarchicalCategories.lvl3']
 * // PetSafe:  ['hierarchicalCategories.lvl0', ..., 'hierarchicalCategories.lvl2']
 */
export function getHierarchicalAttributes(baseAttributeName: string): string[] {
  return Array.from(
    { length: MAX_HIERARCHY_LEVEL + 1 },
    (_, i) => `${baseAttributeName}.lvl${i}`
  );
}

export const LEGACY_ROUTE_STATE_KEYS = [
  'refinementList',
  'hierarchicalMenu',
  'menu',
  'toggle',
  'range',
  'numericMenu',
  'ratingMenu',
  'sortBy',
];
export const HIERARCHICAL_PATH_SEPARATOR = '/';
export const ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR = ' > ';
export const HIERARCHICAL_ATTRIBUTE_LEVEL_PATTERN = /\.lvl(\d+)$/;
export const SEARCH_ROUTE_RESERVED_KEYS = ['tab', 'query', 'sort'];
export const PRODUCT_ROUTE_STATE_OPTIONS = {
  includeHierarchicalMenu: true,
  toggleAttributes: ALGOLIA_BOOLEAN_FACET_ATTRIBUTES,
  attributeRouteKeys: {
    'hierarchicalCategories.lvl0': 'categories',
  },
};
export const COLLECTION_ROUTE_STATE_OPTIONS = {
  includeHierarchicalMenu: false,
  toggleAttributes: ALGOLIA_BOOLEAN_FACET_ATTRIBUTES,
  reservedRouteKeys: ['sort'],
};

export const ARTICLE_ROUTE_STATE_OPTIONS = {
  attributeRouteKeys: {
    'author.name': 'author',
    'categories.title': 'categories',
  },
  hierarchicalAttributes: ['categories.title'],
};

export const INITIAL_STATE_OPTIONS = {
  toggleAttributes: ALGOLIA_BOOLEAN_FACET_ATTRIBUTES,
};

export const IGNORED_URL_PARAMS: readonly (string | RegExp)[] = [
  '_ga',
  '_gl',
  'client_id',
  'gbraid',
  'li_fat_id',
  'promo',
  /^promo[_-]?code$/i,
  'referral_code',
  'wbraid',
  /clid$/,
  /^gad_/i,
  /^hsCtaTracking/i,
  /^mc_/i,
  /^utm_/i,
  /^hubspot_/i,
  /^tw_/i,
  '_kx',
  'ref',
];

/**
 * Checks if the given URL parameter key should not be treated as part
 * of the Algolia search state
 */
export function isIgnoredUrlParam(key: string): boolean {
  for (const pattern of IGNORED_URL_PARAMS) {
    if (typeof pattern === 'string' && key === pattern) {
      return true;
    } else if (pattern instanceof RegExp) {
      pattern.lastIndex = 0;
      if (pattern.test(key)) return true;
    }
  }
  return false;
}

export interface PLPDetectionResult {
  isCurrentPagePLP: boolean;
  cameFromPlp: boolean;
  hasFromPLPParam: boolean;
  isAnyPLPContext: boolean;
}

export function detectPLPContext(): PLPDetectionResult {
  const isCurrentPagePLP =
    globalThis.location?.pathname?.includes('/shop') ?? false;
  const cameFromPlp = globalThis.document?.referrer?.includes('/shop') ?? false;
  const hasFromPLPParam =
    globalThis.location?.search?.includes('fromPLP=true') ?? false;
  const isAnyPLPContext = isCurrentPagePLP || cameFromPlp || hasFromPLPParam;

  return {
    isCurrentPagePLP,
    cameFromPlp,
    hasFromPLPParam,
    isAnyPLPContext,
  };
}

export function getPLPQueryID(fallback?: string | null): string | null {
  return (
    globalThis.sessionStorage?.getItem(PLP_ALGOLIA_QUERY_ID_KEY) ??
    fallback ??
    null
  );
}

export function getLastQueryID(fallback?: string | null): string | null {
  return (
    globalThis.sessionStorage?.getItem(LAST_ALGOLIA_QUERY_ID_KEY) ??
    fallback ??
    null
  );
}

const RATING_SORT_ENABLED_LOCALES: Readonly<Record<string, readonly string[]>> =
  {
    petsafe: [
      'en-us',
      'en-gb',
      'fr-fr',
      'de-de',
      'it-it',
      'es-es',
      'en-au',
      'en-ca',
      'fr-ca',
    ],
    sportdog: ['en-us'],
  };

function getRatingSortBrand(): string | undefined {
  if (isSportdog()) return 'sportdog';
  if (isPetsafe()) return 'petsafe';
  return undefined;
}

function isRatingSortEnabled(locale?: string): boolean {
  if (!locale) return true;
  const brand = getRatingSortBrand();
  if (!brand) return true;
  const allowedLocales = RATING_SORT_ENABLED_LOCALES[brand];
  return allowedLocales?.includes(locale) ?? true;
}

export const getProductSortByItems = (
  indexName: string,
  tt: (
    selector: (s: Record<string, Record<string, string>>) => string
  ) => string,
  locale?: string
) => {
  const items: { label: string; value: string }[] = [
    { label: tt(s => s.searchComponent.sortByRelevance), value: indexName },
    {
      label: tt(s => s.searchComponent.sortByPriceLowToHigh),
      value: `${indexName}_price_asc`,
    },
    {
      label: tt(s => s.searchComponent.sortByPriceHighToLow),
      value: `${indexName}_price_desc`,
    },
  ];

  if (isRatingSortEnabled(locale)) {
    items.push({
      label: tt(s => s.searchComponent.sortByRating),
      value: `${indexName}_rating_desc`,
    });
  }

  items.push(
    {
      label: tt(s => s.searchComponent.sortByBestSelling),
      value: `${indexName}_bestselling_desc`,
    },
    {
      label: tt(s => s.searchComponent.sortByNewest),
      value: `${indexName}_created_at`,
    }
  );

  return items;
};

export const getArticleSortByItems = (
  blogIndexName: string,
  tt: (
    selector: (s: Record<string, Record<string, string>>) => string
  ) => string
) => [
  {
    label: tt(s => s.searchComponent.sortByRelevance),
    value: blogIndexName,
  },
  {
    label: tt(s => s.searchComponent.sortByNewest),
    value: `${blogIndexName}_date_desc`,
  },
  {
    label: tt(s => s.searchComponent.sortByOldest),
    value: `${blogIndexName}_date_asc`,
  },
];

export const COMMA_DECIMAL_LOCALES = new Set([
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'nl',
  'da',
  'fi',
  'nb',
  'nn',
  'sv',
  'pl',
  'cs',
  'sk',
  'hu',
  'ro',
  'bg',
  'hr',
  'sl',
  'et',
  'lv',
  'lt',
  'el',
  'tr',
  'ru',
  'uk',
  'vi',
  'id',
]);

export const HIERARCHICAL_CATEGORY_PREFIXES = [
  'hierarchicalCategories.lvl',
  'thematicCategories.lvl',
  'categories.lvl',
];

export const RATING_STARS: StarRatingItem[] = [
  {
    stars: [true, true, true, true, false],
    label: '≥ 4',
  },
  {
    stars: [true, true, true, false, false],
    label: '≥ 3',
  },
  {
    stars: [true, true, false, false, false],
    label: '≥ 2',
  },
  {
    stars: [true, false, false, false, false],
    label: '≥ 1',
  },
];
